import { useEffect, useMemo, useState } from "react";
import {
  AssignTaskRequest,
  ClioService,
  MatterPreviewResponse,
  TaskPreviewResponse,
  UsersModel,
} from "../../api";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import FilterableSelect from "../../components/basic/FilterableSelect";

const MySwal = withReactContent(Swal);

type Props = {
  onAssigned: () => void;
};

export default function AssignTaskForm({ onAssigned }: Props) {
  const [matterOptions, setMatterOptions] = useState<readonly MatterPreviewResponse[]>([]);
  const [matterValue, setMatterValue] = useState<MatterPreviewResponse | null>(null);
  const [matterInputValue, setMatterInputValue] = useState("");
  const [taskInputValue, setTaskInputValue] = useState("");
  const [taskValue, setTaskValue] = useState<TaskPreviewResponse | null>(null);
  const [taskOptions, setTaskOptions] = useState<readonly TaskPreviewResponse[]>([]);
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setMatterValue(null);
    setMatterInputValue("");
    setTaskValue(null);
    setTaskInputValue("");
    setMatterOptions([]);
    setTaskOptions([]);
    setSelectedUser(null);
  };

  useEffect(() => {
    ClioService.getApiClioClioUsers(true).then(setUsersList);
  }, []);

  const fetchMatters = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioMatters(input).then((response) => {
          const newOptions: MatterPreviewResponse[] = matterValue ? [matterValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setMatterOptions(newOptions);
        });
      }, 200),
    [matterValue]
  );

  useEffect(() => {
    if (matterInputValue === "") {
      setMatterOptions(matterValue ? [matterValue] : []);
      return;
    }
    fetchMatters(matterInputValue);
  }, [matterValue, matterInputValue, fetchMatters]);

  const fetchTasks = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioTaskTypes(input).then((response) => {
          const newOptions: TaskPreviewResponse[] = taskValue ? [taskValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setTaskOptions(newOptions);
        });
      }, 200),
    [taskValue]
  );

  useEffect(() => {
    if (taskInputValue === "") {
      setTaskOptions(taskValue ? [taskValue] : []);
      return;
    }
    fetchTasks(taskInputValue);
  }, [taskValue, taskInputValue, fetchTasks]);

  const canSubmit =
    matterValue?.id != null && taskValue?.id != null && selectedUser != null;

  const assignTask = () => {
    if (!canSubmit) {
      MySwal.fire({
        icon: "warning",
        title: <p>Please complete all required fields.</p>,
      });
      return;
    }

    const request = {
      matterId: +matterValue!.id!,
      taskTypeId: +taskValue!.id!,
      assigneeId: selectedUser!,
    } as AssignTaskRequest;

    setSubmitting(true);
    ClioService.postApiClioAssignTask(request)
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: <p>Task assigned successfully.</p>,
        });
        onAssigned();
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error assigning task.</p>,
        })
      )
      .finally(() => setSubmitting(false));
  };

  const assigneeOptions = useMemo(
    () =>
      usersList.map((user) => ({
        value: user.id!,
        label: user.name ?? "",
      })),
    [usersList]
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Assign Clio Task
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Select a matter, task, and assignee to create the assignment.
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "flex-start" }}
      >
        <Autocomplete
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
          getOptionLabel={(option) =>
            (typeof option === "string" ? option : option.description) as string
          }
          filterOptions={(x) => x}
          options={matterOptions}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={matterValue}
          noOptionsText="Start typing matter"
          onChange={(_, selectedValue) => setMatterValue(selectedValue)}
          onInputChange={(_, newInputValue) => setMatterInputValue(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Matter" required fullWidth size="small" />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography variant="body2">{option.description}</Typography>
            </li>
          )}
        />

        <Autocomplete
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
          getOptionLabel={(option) =>
            (typeof option === "string" ? option : option.name) as string
          }
          filterOptions={(x) => x}
          options={taskOptions}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={taskValue}
          noOptionsText="Start typing task"
          onChange={(_, selectedValue) => setTaskValue(selectedValue)}
          onInputChange={(_, newInputValue) => setTaskInputValue(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Task" required fullWidth size="small" />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography variant="body2">{option.name}</Typography>
            </li>
          )}
        />

        <FilterableSelect
          label="Assignee"
          fullWidth
          required
          includeAll={false}
          size="small"
          value={selectedUser ?? undefined}
          options={assigneeOptions}
          onChange={(userId) => setSelectedUser(userId == null ? null : Number(userId))}
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
        />
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2.5 }}>
        <Button
          onClick={resetForm}
          variant="outlined"
          color="primary"
          disabled={submitting}
        >
          Clear
        </Button>
        <Button
          onClick={assignTask}
          variant="contained"
          color="primary"
          disabled={!canSubmit || submitting}
          startIcon={
            submitting ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          Assign Task
        </Button>
      </Stack>
    </Paper>
  );
}
